<template>
    <v-card height="100%" class="tw-flex tw-flex-col">
        <v-card-text class="ordinary--text tw-grow">
            <v-row class="tw-justify-between">
                <!-- Left Column -->
                <v-col cols="7" sm="8">
                    <!-- Package Title -->
                    <div class="tw-mb-6">
                        <div
                            :style="{
                                backgroundColor: `${studio.primaryColor}2e`,
                            }"
                            class="tw-inline-block tw-rounded-md tw-px-4 tw-py-2 tw-text-webAppFontColor"
                        >
                            {{ packageItem.name }}
                        </div>
                    </div>

                    <div class="tw-mb-6">
                        <p class="tw-mb-2 tw-font-bold">
                            {{ packageItem.classNumbers }}
                            {{ $t('message.credits') }}
                            <span
                                class="tw-font-normal tw-text-gray-500 night:tw-text-gray-200"
                            >
                                {{ packageDescription }}
                            </span>
                        </p>
                    </div>

                    <div
                        v-if="!isBuyable && packageItem.hasStarted"
                        class="tw-mb-6"
                    >
                        <p
                            class="tw-font-normal tw-text-gray-500 night:tw-text-gray-200"
                        >
                            {{ expiresOn }}<br />
                            {{ purchasedOn }}<br />
                            {{ packageStatus }}
                        </p>
                    </div>

                    <div
                        v-if="!isBuyable && !packageItem.hasStarted"
                        class="tw-mb-6"
                    >
                        <p
                            class="tw-font-normal tw-text-gray-500 night:tw-text-gray-200"
                        >
                            {{ newPurchaseExpiry[0] }}<br />
                            {{ newPurchaseExpiry[1] }}
                        </p>
                    </div>

                    <div v-if="isBuyable">
                        <p
                            class="tw-mb-0 tw-font-normal tw-text-gray-500 night:tw-text-gray-200"
                        >
                            {{ $t('message.classesEligible') }}
                        </p>
                        <p
                            class="tw-font-bold tw-text-gray-600 night:tw-text-gray-300"
                        >
                            {{ classesRestrictions }}
                        </p>
                    </div>
                </v-col>

                <!-- Right Column -->
                <v-col cols="5" sm="4" class="tw-flex tw-flex-col tw-items-end">
                    <h3 class="tw-mb-6 tw-text-right">
                        {{ `${studio.currency}${packageItem.price}` }}
                    </h3>

                    <div v-if="showChangePaymentMethodBtn" class="tw-w-full">
                        <v-btn
                            @click="showPaymentMethodsDialog = true"
                            outlined
                            class="tw-mt-2 tw-w-full"
                        >
                            <v-icon class="tw-mr-2">mdi-credit-card</v-icon>
                            {{ $t('message.changeCard') }}
                        </v-btn>
                    </div>

                    <PackageCancelButton
                        v-if="isRecurringPackageUserCancellable"
                        class="tw-mt-2 tw-w-full"
                        :packageId="packageItem.id"
                        @cancelStatus="cancelStatus"
                    />
                </v-col>
            </v-row>
        </v-card-text>

        <div v-if="isBuyable && packageItem.isGroupBookable" class="tw-mb-4">
            <div class="tw-flex tw-items-center tw-gap-2 tw-px-4">
                <img
                    src="./icons/group-booking.svg"
                    alt="Group Booking"
                    class="tw-size-5"
                />
                <span class="tw-text-green-500">{{
                    $t('message.eligibleForGroupBooking', {
                        pax: packageItem.numGroupBookable,
                    })
                }}</span>
            </div>
        </div>

        <!-- PaymentMethods Dialog -->
        <v-dialog
            v-model="showPaymentMethodsDialog"
            max-width="600px"
            @click:outside="showPaymentMethodsDialog = false"
            @keydown:esc="showPaymentMethodsDialog = false"
        >
            <v-card class="tw-flex tw-flex-col">
                <v-icon
                    @click="showPaymentMethodsDialog = false"
                    class="tw-mr-4 tw-mt-4 tw-self-end"
                >
                    mdi-close
                </v-icon>
                <PaymentMethods :packageId="packageItem.id" />
            </v-card>
        </v-dialog>

        <v-spacer></v-spacer>

        <!-- Bottom Buttons -->
        <div class="tw-flex tw-justify-end tw-gap-2 tw-p-4">
            <PackageInfo
                class="tw-flex"
                :buttonText="$t('message.viewMore')"
                :packageItem="packageItem"
                :isBuyable="isBuyable"
                @sharePackage="sharePackage"
            />
            <CheckoutWindow
                v-if="isBuyable"
                :packageItem="packageItem"
                :buttonText="$t('message.buyNow')"
                :isPurchasePackage="true"
                :familyAccounts="familyAccounts"
            />
        </div>
    </v-card>
</template>

<script>
import PackageInfo from '@/components/packages/PackageInfo'
import CheckoutWindow from '@/components/checkout/CheckoutWindow'
import PaymentMethods from '@/views/PaymentMethods'
import PackageCancelButton from './PackageCancelButton.vue'
import { dateStringFromTimestamp, convertDateFormat } from '@/util/dateformat'
import { STUDIO_MODULE_NAME } from '@/store/moduleNames'
import { mapGetters } from 'vuex'
import { WIDGET_ROUTE } from '@/constants'
import { getRestrictionList } from '@/util/classes/classFormat'

export default {
    components: {
        PackageInfo,
        CheckoutWindow,
        PaymentMethods,
        PackageCancelButton,
    },
    data() {
        return {
            showPaymentMethodsDialog: false,
        }
    },
    props: {
        packageItem: Object,
        isBuyable: Boolean,
        familyAccounts: Array,
    },
    computed: {
        ...mapGetters({
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
        }),
        renewDurationType() {
            const packageItem = this.packageItem
            const recurring = packageItem.renewDurationType
            if (!recurring) {
                return ''
            }
            const renewalDurationTypeLen = recurring.length
            const lastRenewalDurationLetter =
                recurring[renewalDurationTypeLen - 1]
            if (lastRenewalDurationLetter === 's') {
                return recurring
                    .substring(0, renewalDurationTypeLen - 1)
                    .toLowerCase()
            } else {
                return recurring.toLowerCase()
            }
        },
        formattedDurationType() {
            const packageItem = this.packageItem
            const duration =
                packageItem.durationOriginal ?? packageItem.duration
            const durationType = packageItem.durationType ?? 'Days'

            if (duration == 1) {
                return durationType.slice(0, -1).toLowerCase()
            } else {
                return durationType.toLowerCase()
            }
        },
        packageDescription() {
            const packageItem = this.packageItem
            const recurring = packageItem.renewDuration
            if (this.isBuyable) {
                let criteriaMessage = this.$t('message.firstClass')
                if (
                    packageItem.expiryImmediateCriterion === 'expiry-immediate'
                ) {
                    criteriaMessage = this.$t('message.purchase')
                }
                if (
                    packageItem.expiryImmediateCriterion === 'expiry-fixed' &&
                    packageItem.fixedStartDate
                ) {
                    criteriaMessage = this.$t('message.fixedDate', {
                        fixedDate: convertDateFormat(
                            packageItem.fixedStartDate,
                            'YYYY-MM-DD',
                            'D MMM YYYY'
                        ),
                    })
                }
                if (recurring) {
                    const numCycles = packageItem.numCycles
                    const emptyValuesToCheck = [undefined, null, '', '0']
                    const hasCycles = !emptyValuesToCheck.includes(
                        packageItem.numCycles
                    )
                    const renewalDuration = `${packageItem.renewDuration} ${this.renewDurationType}`
                    const stringValue = hasCycles
                        ? this.$t('message.packageRenewWithCycles', {
                              renewalDuration: renewalDuration,
                              expiresAfter:
                                  packageItem.renewDuration * numCycles,
                              renewDurationType: this.renewDurationType,
                          })
                        : this.$t('message.packageRenewWithoutCycles', {
                              renewalDuration: renewalDuration,
                          })
                    return `${stringValue}`
                } else {
                    return `${this.$t('message.packageValidFor', {
                        duration:
                            packageItem.durationOriginal ??
                            packageItem.duration,
                        durationType: this.formattedDurationType,
                        criteriaMessage: criteriaMessage,
                    })}`
                }
            }
            return `${packageItem.classesLeft} / ${
                packageItem.numberClasses
            } ${this.$t('message.classesLeft')}`
        },
        expiresOn() {
            return this.$t('message.expiresOn', {
                date: dateStringFromTimestamp(this.packageItem.expiryDate),
            })
        },
        purchasedOn() {
            return this.$t('message.purchasedOn', {
                date: dateStringFromTimestamp(this.packageItem.purchaseDate),
            })
        },
        packageStatus() {
            const packageItem = this.packageItem
            if (packageItem.isExpired) {
                return this.$t('message.expired')
            }
            return this.$t('message.classesRemaining', {
                classesLeft: packageItem.classesLeft,
                numberClasses: packageItem.numberClasses,
            })
        },
        newPurchaseExpiry() {
            const packageItem = this.packageItem
            return [
                this.$t('message.validFrom', {
                    date: dateStringFromTimestamp(packageItem.startDate),
                }),
                this.$t('message.validUntil', {
                    date: dateStringFromTimestamp(packageItem.expiryDate),
                }),
            ]
        },
        classesRestrictions() {
            const packageItem = this.packageItem
            if (
                packageItem.hasClassRestriction &&
                packageItem.classes?.length < this.studio.classes.length
            ) {
                return getRestrictionList(
                    this.studio.classes.map((c) => c.className),
                    packageItem.classes,
                    this.$t.bind(this)
                )
            }
            return this.$t('message.classesAll')
        },
        isWidget() {
            return this.$route.hash === WIDGET_ROUTE
        },
        isRecurringPackage() {
            return this.packageItem.isSubscription
        },
        isRecurringPackageUserCancellable() {
            return (
                this.isRecurringPackage &&
                !this.isBuyable &&
                this.packageItem.isUserCancellable &&
                this.packageItem.status === 'active'
            )
        },
        showChangePaymentMethodBtn() {
            return (
                this.isRecurringPackage &&
                !this.isBuyable &&
                this.packageItem.status === 'active'
            )
        },
    },
    methods: {
        sharePackage() {
            this.$emit('sharePackage')
        },
        cancelStatus(isSuccess, error) {
            this.$emit('cancelStatus', isSuccess, error)
        },
    },
}
</script>

<style scoped>
.v-card {
    border-radius: 8px;
}
</style>
